import { LoadingSpinner, PageHeaderWrapper, useModalState } from "@clipboard-health/ui-components";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { RootPaths } from "@src/appV2/App/paths";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useCallback, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useGetPayoutSettings } from "../../Accounts/PayoutSettings/api/useGetPayoutSettings";
import { useGoToStripeOnboarding } from "../../Accounts/PayoutSettings/hooks/useGoToStripeOnboarding";
import { PageHeaderTopRow } from "../../components/PageHeaderTopRow";
import { PageWrapper } from "../../components/PageWrapper";
import { useGetPaymentServiceAccount } from "../../Onboarding/api/useGetPaymentServiceAccount";
import { useUpdatePaymentServiceAccount } from "../../Onboarding/api/useUpdatePaymentServiceAccount";
import { SHIFT_DISCOVERY_LIST_PATH } from "../../ShiftDiscovery/paths";
import { IncompleteStripeSetupPageContent } from "./components/IncompleteStripeSetupPageContent";
import { StripeAccountVerifiedPageContent } from "./components/StripeAccountVerifiedPageContent";
import { StripeAccountVerifyingPageContent } from "./components/StripeAccountVerifyingPageContent";
import { StripeOnboardingCompletePageContent } from "./components/StripeOnboardingCompletePageContent";
import { StripeOnboardingIntroPageContent } from "./components/StripeOnboardingIntroPageContent";
import { StripeOnboardingPayoutSettingsBottomSheet } from "./components/StripeOnboardingPayoutSettingsBottomSheet";

const POLLING_INTERVAL_MS = 5000;

export function StripeOnboardingPage() {
  const history = useHistory();
  const location = useLocation();

  const isStripeOnboardingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_NEW_STRIPE_ONBOARDING, {
    defaultValue: false,
  });

  const { goToStripeOnboarding, isGoToStripeOnboardingLoading } = useGoToStripeOnboarding();
  const { mutateAsync: updatePaymentServiceAccount, isLoading: isCompleteOnboardingLoading } =
    useUpdatePaymentServiceAccount({
      meta: {
        logErrorMessage: APP_V2_APP_EVENTS.UPDATE_PAYMENT_SERVICE_ACCOUNT_FAILURE,
        userErrorMessage: "Something went wrong, please try again.",
      },
    });

  const { data: { stripeOnboardingStage } = {}, isLoading } = useGetPaymentServiceAccount({
    refetchInterval: POLLING_INTERVAL_MS,
    staleTime: 0,
  });

  const { data: payoutSettings, isLoading: isPayoutSettingsLoading } = useGetPayoutSettings();

  const isOnboardingFullyCompleted =
    stripeOnboardingStage === "completed" &&
    isDefined(payoutSettings?.attributes.preferredExternalAccountStripeId);

  const payoutSettingsBottomSheetModalState = useModalState();
  const shiftDiscoveryPath = `${RootPaths.APP_V2_HOME}/${SHIFT_DISCOVERY_LIST_PATH}`;
  const goBackWithFallback = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_EXITED, { stage: stripeOnboardingStage });
    // If there's browser history entry, preserve expected back behavior.
    if (isDefined(location.key)) {
      history.goBack();
      return;
    }

    history.push(shiftDiscoveryPath);
  }, [history, location.key, shiftDiscoveryPath, stripeOnboardingStage]);

  const shouldExit = !isStripeOnboardingEnabled || (!isLoading && isNil(stripeOnboardingStage));

  const handleGoToStripeFromInitial = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_GO_TO_STRIPE_CLICKED);

    goToStripeOnboarding();
  }, [goToStripeOnboarding]);

  const handleReturnToStripeFromWaiting = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_WAITING_RETURN_TO_STRIPE_CLICKED);

    goToStripeOnboarding();
  }, [goToStripeOnboarding]);

  const handleReturnToStripeFromVerificationFailed = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_VERIFICATION_FAILED_RETURN_TO_STRIPE_CLICKED);

    goToStripeOnboarding();
  }, [goToStripeOnboarding]);

  useLogEffect(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_INITIAL_VIEWED, undefined, {
    enabled: !isLoading && stripeOnboardingStage === "initial",
  });
  useLogEffect(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_WAITING_VIEWED, undefined, {
    enabled: !isLoading && stripeOnboardingStage === "waiting",
  });
  useLogEffect(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_VERIFICATION_FAILED_VIEWED, undefined, {
    enabled: !isLoading && stripeOnboardingStage === "validation_failed",
  });
  useLogEffect(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_VERIFICATION_SUCCEEDED_VIEWED, undefined, {
    enabled:
      !isLoading &&
      !isPayoutSettingsLoading &&
      stripeOnboardingStage === "completed" &&
      isNil(payoutSettings?.attributes.preferredExternalAccountStripeId),
  });

  useEffect(() => {
    if (shouldExit) {
      goBackWithFallback();
    }
  }, [goBackWithFallback, shouldExit]);

  if (shouldExit) {
    return null;
  }

  return (
    <PageWrapper variant="tertiary">
      <PageHeaderWrapper variant="tertiary">
        <PageHeaderTopRow
          title="Set up Stripe"
          hideBackButton={isOnboardingFullyCompleted}
          fallbackGoBackPath={shiftDiscoveryPath}
          onClickBack={goBackWithFallback}
        />
      </PageHeaderWrapper>

      {isLoading && (
        <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
          <LoadingSpinner size="large" />
        </Box>
      )}
      {!isLoading && stripeOnboardingStage === "initial" && (
        <StripeOnboardingIntroPageContent
          isSetUpStripeLoading={isGoToStripeOnboardingLoading}
          setUpStripe={handleGoToStripeFromInitial}
        />
      )}
      {!isLoading && stripeOnboardingStage === "waiting" && (
        <StripeAccountVerifyingPageContent
          isReturnToStripeLoading={isGoToStripeOnboardingLoading}
          onReturnToStripeClick={handleReturnToStripeFromWaiting}
          onExploreTheAppClick={goBackWithFallback}
        />
      )}
      {!isLoading && stripeOnboardingStage === "validation_failed" && (
        <IncompleteStripeSetupPageContent
          isCompleteStripeSetupLoading={isGoToStripeOnboardingLoading}
          onCompleteStripeSetupClick={handleReturnToStripeFromVerificationFailed}
        />
      )}
      {!isLoading && stripeOnboardingStage === "completed" && isPayoutSettingsLoading && (
        <Box sx={{ display: "flex", flex: 1, alignItems: "center", justifyContent: "center" }}>
          <LoadingSpinner size="large" />
        </Box>
      )}
      {!isLoading &&
        stripeOnboardingStage === "completed" &&
        !isPayoutSettingsLoading &&
        isNil(payoutSettings?.attributes.preferredExternalAccountStripeId) && (
          <StripeAccountVerifiedPageContent
            onConfigurePayoutSettingsClick={payoutSettingsBottomSheetModalState.openModal}
          />
        )}
      {stripeOnboardingStage === "completed" && (
        <StripeOnboardingPayoutSettingsBottomSheet
          modalState={payoutSettingsBottomSheetModalState}
        />
      )}
      {!isLoading &&
        stripeOnboardingStage === "completed" &&
        !isPayoutSettingsLoading &&
        isDefined(payoutSettings?.attributes.preferredExternalAccountStripeId) &&
        isDefined(payoutSettings?.destinationDetails) && (
          <StripeOnboardingCompletePageContent
            payoutSchedule={payoutSettings.attributes.payoutSchedule}
            payoutFee={
              payoutSettings.attributes.payoutSchedule === "instant"
                ? payoutSettings.meta.instantPayoutFee
                : 0
            }
            destination={payoutSettings.destinationDetails}
            isStartBookingLoading={isCompleteOnboardingLoading}
            onStartBooking={async () => {
              try {
                await updatePaymentServiceAccount({ isOnboardingCompleted: true });
                logEvent(APP_V2_USER_EVENTS.PAYMENTS_ONBOARDING_COMPLETED, {
                  selectedPayoutSchedule: payoutSettings.attributes.payoutSchedule,
                  selectedAccountType: payoutSettings.destinationDetails?.type,
                });
                history.push(shiftDiscoveryPath);
              } catch {
                // Error toast is shown via the mutation's meta.userErrorMessage
              }
            }}
          />
        )}
    </PageWrapper>
  );
}
